import { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import PageHeader from '../../components/layout/PageHeader';
import { tokens } from '../../components/forms/tokens';
import { formatDateTime } from '../../lib/datetime';
import { fullName } from '../../lib/fullName';
import StatusBadge from '../../components/StatusBadge';
import { useDebouncedValue } from '../../hooks/useDebouncedValue';
import { useFlashTimeout } from '../../hooks/useFlashTimeout';
import adminService, {
  type AdminUserSummary,
  type ListUsersParams,
} from '../../services/admin.service';
import {
  ActionDialog,
  RowMenu,
  extractError,
  type ActionKind,
  type ActionState,
} from './_userActions';

/** Action set for client rows. Excludes change_role, assign_manager,
 *  and reassign_clients — those only make sense for staff. The rest are
 *  pure security operations (lock/unlock/sessions/TOTP/password) and
 *  operate by user_id, so they reuse the same admin endpoints. */
const CLIENT_ACTIONS: ReadonlySet<ActionKind> = new Set<ActionKind>([
  'deactivate',
  'activate',
  'force_password',
  'unlock',
  'revoke_sessions',
  'change_timezone',
  'totp_reset',
]);

export default function AdminClients(): JSX.Element {
  const [users, setUsers] = useState<AdminUserSummary[]>([]);
  // How many match in all: the list shows the first hundred and says when there are more.
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState<'all' | 'active' | 'inactive'>('all');
  const [action, setAction] = useState<ActionState | null>(null);
  const [flash, setFlash] = useState<string | null>(null);

  const debouncedSearch = useDebouncedValue(search);

  const reload = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const params: ListUsersParams = { limit: 100, role: 'client' };
      if (debouncedSearch.trim()) params.search = debouncedSearch.trim();
      if (statusFilter !== 'all') params.is_active = statusFilter === 'active';
      const res = await adminService.listUsers(params);
      setUsers(res.items);
      setTotal(res.total);
    } catch (e) {
      setError(extractError(e));
    } finally {
      setLoading(false);
    }
  }, [debouncedSearch, statusFilter]);

  useEffect(() => {
    reload();
  }, [reload]);

  useFlashTimeout(flash, setFlash);

  const handleDone = (msg: string): void => {
    setFlash(msg);
    setAction(null);
    reload();
  };

  return (
    <>
      <PageHeader
        title="Clients"
        subtitle="Client accounts (email + password). Security operations only — KYC, onboarding, and engagement live on the client profile."
      />

      {flash && (
        <div
          className="mb-4 p-3 rounded-lg text-sm"
          style={{ background: tokens.successBg, border: `1px solid ${tokens.successBorder}`, color: tokens.successText }}
        >
          {flash}
        </div>
      )}

      <div
        className="rounded-2xl p-5 mb-5 flex flex-wrap gap-3 items-end"
        style={{ background: tokens.surface }}
      >
        <label className="flex-1 min-w-[220px]">
          <span className="block text-xs font-semibold mb-1" style={{ color: tokens.textLabel }}>Search</span>
          <input
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="email or name"
            className="w-full px-3 py-2 rounded-lg text-sm"
            style={{ border: `1px solid ${tokens.border}` }}
          />
        </label>
        <label>
          <span className="block text-xs font-semibold mb-1" style={{ color: tokens.textLabel }}>Status</span>
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value as 'all' | 'active' | 'inactive')}
            className="px-3 py-2 rounded-lg text-sm"
            style={{ border: `1px solid ${tokens.border}` }}
          >
            <option value="all">All</option>
            <option value="active">Active</option>
            <option value="inactive">Inactive</option>
          </select>
        </label>
      </div>

      {error && (
        <div
          className="mb-4 p-3 rounded-lg text-sm"
          style={{ background: tokens.errorBg, border: `1px solid ${tokens.errorBorder}`, color: tokens.errorText }}
        >
          {error}
        </div>
      )}

      <div className="rounded-2xl p-5 overflow-x-auto" style={{ background: tokens.surface }}>
        <table className="w-full text-sm">
          <thead>
            <tr style={{ color: tokens.textMuted, borderBottom: `1px solid ${tokens.border}` }}>
              <th className="text-left py-2 pr-3">Client</th>
              <th className="text-left py-2 pr-3">Status</th>
              <th className="text-left py-2 pr-3">Assigned agent</th>
              <th className="text-left py-2 pr-3">Security</th>
              <th className="text-left py-2 pr-3">Last login</th>
              <th className="text-right py-2 pr-3">Actions</th>
            </tr>
          </thead>
          <tbody>
            {!loading && total > users.length && (
              <tr>
                <td colSpan={6} className="py-2 text-center text-xs" style={{ color: tokens.textMuted }}>
                  Showing the first {users.length} of {total} clients. Search to find the rest.
                </td>
              </tr>
            )}
            {loading && (
              <tr><td colSpan={6} className="py-6 text-center" style={{ color: tokens.textMuted }}>Loading…</td></tr>
            )}
            {!loading && users.length === 0 && (
              <tr><td colSpan={6} className="py-6 text-center" style={{ color: tokens.textMuted }}>No clients match the current filters.</td></tr>
            )}
            {users.map((u) => {
              const locked = u.lockout_until && new Date(u.lockout_until) > new Date();
              return (
                <tr key={u.id} style={{ borderBottom: `1px solid ${tokens.border}` }}>
                  <td className="py-3 pr-3">
                    <div className="font-medium" style={{ color: tokens.textPrimary }}>{fullName(u)}</div>
                    <div className="text-xs font-mono" style={{ color: tokens.textMuted }}>{u.email}</div>
                  </td>
                  <td className="py-3 pr-3">
                    <StatusBadge tone={u.is_active ? 'success' : 'danger'}>
                      {u.is_active ? 'Active' : 'Inactive'}
                    </StatusBadge>
                  </td>
                  <td className="py-3 pr-3 text-xs" style={{ color: tokens.textMuted }}>
                    {u.assigned_agent_name ?? (
                      <span style={{ fontStyle: 'italic' }}>unassigned</span>
                    )}
                  </td>
                  <td className="py-3 pr-3 text-xs" style={{ color: tokens.textMuted }}>
                    <div>TOTP: {u.totp_enabled ? 'on' : 'off'}</div>
                    <div>Failed: {u.failed_attempts}{locked ? ' · LOCKED' : ''}</div>
                    <div>TZ: {u.timezone ?? <span style={{ fontStyle: 'italic' }}>not set</span>}</div>
                    {u.must_change_password && <div style={{ color: tokens.errorText }}>Must change pwd</div>}
                  </td>
                  <td className="py-3 pr-3 text-xs" style={{ color: tokens.textMuted }}>
                    {formatDateTime(u.last_login_at)}
                  </td>
                  <td className="py-3 pr-3">
                    <div className="flex items-center gap-2 justify-end">
                      <Link
                        to={`/manager/clients/${u.id}`}
                        className="px-3 py-1.5 rounded-md text-xs font-semibold"
                        style={{ background: tokens.surfaceMuted, color: tokens.textPrimary }}
                      >
                        Open profile
                      </Link>
                      <RowMenu
                        user={u}
                        allowedActions={CLIENT_ACTIONS}
                        onPick={(kind) => setAction({ kind, target: u })}
                      />
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {action && (
        <ActionDialog
          state={action}
          onClose={() => setAction(null)}
          onDone={handleDone}
        />
      )}
    </>
  );
}
